import { useNavigate, useParams } from "react-router-dom";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import styles from "./PlansAdministrationPlan.module.scss";
import {
  IPlansAdministrationPlanModel,
  IPlanUserModel,
} from "./models/plansAdministrationPlanModel";
import { ReactNode, useMemo } from "react";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { Icon, IconType } from "../../../../common/icons/Icon";
import {
  constructEditPlanHref,
  constructFamilyHref,
  constructUserHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { classNames } from "primereact/utils";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { PlanConsumerType } from "../../../../common/enums";
import { PlanConsumerTypesLabels } from "../../../../common/labels";
import { PriceDisplay } from "../../../../common/components/PriceDisplay/PriceDisplay";
import { useAppContext } from "../../../../AppContext";

export const PlansAdministrationPlan = () => {
  const { id } = useParams();

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructEditPlanHref(Number(id)),
    rawPath: routePaths.planEdit,
  });

  const { data: result, isLoading } =
    useApiQuery<IPlansAdministrationPlanModel>({
      queryKey: [endpointsMetadata.plan.getPlan, id],
      endpoint: `${endpointsMetadata.plan.getPlan}?planId=${id}`,
      enabled: id !== undefined,
      globalIsLoading: false,
    });

  const currentPlan = useMemo(() => result, [result]);

  const navigate = useNavigate();

  const backToPlans = () => {
    navigate(routePaths.plans);
  };

  const { selectedStudio } = useAppContext();

  const consumerTypeLabel =
    PlanConsumerTypesLabels[currentPlan?.consumerType ?? PlanConsumerType.User];

  if (!currentPlan && !isLoading) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такъв план."}
        buttonText={"Обратно към списъка с планове"}
        onButtonClick={backToPlans}
      />
    );
  }

  return (
    <div className={styles.root}>
      <DefaultSection>
        <div className={styles.root_title}>
          <SkeletonItem isLoading={isLoading} value={currentPlan?.name} />
        </div>
        <div className={styles.root_subtitle}>
          <SkeletonItem
            isLoading={isLoading}
            value={`#${currentPlan?.id}`}
            width={100}
          />
        </div>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        {selectedStudio?.studioUsesDifferentPlanTypes && (
          <div className={styles.root_info_section_pair}>
            <p className={styles.root_info_section_pair_left}>Тип на плана:</p>
            <SkeletonItem
              isLoading={isLoading}
              width={"20%"}
              value={currentPlan?.type}
            />
          </div>
        )}

        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>Целева група:</p>
          <SkeletonItem
            isLoading={isLoading}
            width={"20%"}
            value={consumerTypeLabel}
          />
        </div>

        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>
            Разрешен брой посещения:
          </p>
          <SkeletonItem
            isLoading={isLoading}
            width={"20%"}
            value={
              currentPlan?.numberOfVisits
                ? `${currentPlan?.numberOfVisits} бр.`
                : "∞"
            }
          />
        </div>

        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>Валидност:</p>
          <SkeletonItem
            isLoading={isLoading}
            value={currentPlan?.duration}
            width={150}
          />
        </div>
        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>Цена:</p>
          <SkeletonItem
            isLoading={isLoading}
            value={
              currentPlan?.priceInEur !== undefined ? (
                <PriceDisplay minPrice={currentPlan.priceInEur} />
              ) : undefined
            }
            width={200}
          />
        </div>

        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>Създаден на:</p>
          <SkeletonItem
            isLoading={isLoading}
            value={
              currentPlan?.createdOn
                ? dateUtility.formatDate(currentPlan?.createdOn)
                : undefined
            }
            width={180}
          />
        </div>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>
            Брой активни клиенти:
          </p>
          <SkeletonItem
            width={100}
            isLoading={isLoading}
            value={currentPlan?.howManyUsersUseIt}
          />
        </div>
        <div className={styles.root_info_section_pair}>
          <p className={styles.root_info_section_pair_left}>
            Оборот до момента:
          </p>
          <SkeletonItem
            isLoading={isLoading}
            width={250}
            value={
              currentPlan?.grossRevenueForThisPlanInEur ? (
                <PriceDisplay
                  minPrice={currentPlan.grossRevenueForThisPlanInEur}
                />
              ) : (
                "-"
              )
            }
          />
        </div>
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        className={styles.list}
        title="Потребители"
        noItemsText="Не са открити потребители."
        itemTemplate={(item, _index, actionButtons, className) => {
          return (
            <PlansAdministrationPlanUserItem
              className={className}
              user={item}
              isLoading={isLoading}
              actionButtons={actionButtons}
              planConsumerType={result?.consumerType}
            />
          );
        }}
        items={currentPlan?.users ?? []}
        swipeLeftActions={[
          {
            method: "delete",
            iconType: IconType.DeleteIcon,
            enablePrompt: true,
            promptMessage:
              "Сигурни ли сте, че искате да прекратите картата на този потребител? Това действие не може да бъде ",
            endpoint: endpointsMetadata.plan.toggleSubscriptionDeletionStatus,
            additionalParams: (item: IPlanUserModel) => `subscriptionId=${item.subscriptionId}`,
            validator: (item: IPlanUserModel) => !item.isDeleted,
            invalidateKeys: [[endpointsMetadata.plan.getPlan]],
          },
        ]}
        swipeRightActions={[
          {
            type: "edit",
            iconType: IconType.EditIcon,
            endpoint: routePaths.userEdit,
          },
        ]}
        enableSearch
        fullHeight
      />
    </div>
  );
};

interface IPlansAdministrationPlanUserItemProps {
  className?: string;
  user?: IPlanUserModel;
  isLoading: boolean;
  actionButtons?: ReactNode;
  planConsumerType?: PlanConsumerType;
}

const PlansAdministrationPlanUserItem = ({
  className,
  user: member,
  isLoading,
  actionButtons,
  planConsumerType,
}: IPlansAdministrationPlanUserItemProps) => {
  const navigate = useNavigate();

  const onUserClick = () => {
    const isFamilyPlan = planConsumerType === PlanConsumerType.Family;

    if (member) {
      let href = "";

      if (!isFamilyPlan) {
        href = constructUserHref(member.id);
      } else {
        href = constructFamilyHref(member.id);
      }

      navigate(href);
    }
  };
  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_deleted]: !isLoading && member?.isDeleted,
        [styles.item_expired]: !isLoading && !member?.isDeleted && !member?.isActive,
      })}
      onClick={onUserClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          width={"50%"}
          value={
            <div className={styles.item_left_name}>
              {member?.name}
              <span className={styles.item_left_name_id}>
                (ID: {member?.id})
              </span>
            </div>
          }
        />

        <SkeletonItem
          isLoading={isLoading}
          value={<div className={styles.item_left_email}>{member?.email}</div>}
          width={"80%"}
        />

        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_phone}>{member?.phoneNumber}</div>
          }
          width={"60%"}
        />
      </div>
      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
